import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { showErrorToast, showSuccessToast } from '../../../../utils/toastUtils';
import { BREAK_STEPS, BREAK_ACTIONS, BREAK_TYPES } from '../../../../utils/constants/breakConstants';
import { getBackButtonAction } from './breakOptionsModalHelpers';
import OptionsStep from './breakModalSteps/OptionsStep';
import WaiverBreakSelectionStep from './breakModalSteps/WaiverBreakSelectionStep';
import WaiverConfirmationStep from './breakModalSteps/WaiverConfirmationStep';
import {
  ModalContainer,
  ModalHeader,
  ModalContent,
  CloseButtonContainer,
  BackButtonContainer,
  StyledIconButton,
} from './BreakOptionsModal.styles';
import { getFunctions, httpsCallable } from 'firebase/functions';
import StartBreakConfirmationStep from './breakModalSteps/StartBreakConfirmationStep';

const BreakOptionsModal = ({
  open,
  onClose,
  shiftId,
  fetchBreaks,
  onBreakAction,
  onWaiveSubmit,
  isLoading = false,
  showRestButton,
  showBreakButton,
}) => {
  const [currentStep, setCurrentStep] = useState(BREAK_STEPS.OPTIONS);
  const [breaks, setBreaks] = useState([]);
  const [selectedBreak, setSelectedBreak] = useState(null);
  const [reason, setReason] = useState('');
  const [isLoadingBreaks, setIsLoadingBreaks] = useState(true);
  const [isStartingMealBreak, setIsStartingMealBreak] = useState(false);

  const loadBreaks = useCallback(async () => {
    try {
      setIsLoadingBreaks(true);
      const result = await fetchBreaks(shiftId);
      const availableBreaks = Array.isArray(result.payload)
        ? result.payload.filter((breakItem) => !breakItem.start && !breakItem.waived)
        : [];
      setBreaks(availableBreaks);
    } catch (err) {
      showErrorToast('Failed to load break information');
    } finally {
      setIsLoadingBreaks(false);
    }
  }, [shiftId, fetchBreaks]);

  const getBreakStartViolations = useCallback(async () => {
    try {
      const getBreakStartViolations = httpsCallable(getFunctions(), 'getBreakStartViolations');
      const response = await getBreakStartViolations({
        sid: shiftId,
        breakType: BREAK_TYPES.MEAL,
      });
      return response;
    } catch (error) {
      console.error('Error calling getBreakStartViolations:', error);
      throw error;
    }
  }, [shiftId]);

  useEffect(() => {
    if (open && shiftId) {
      loadBreaks();
    }
  }, [open, shiftId, loadBreaks]);

  const handleWaiverBreakSelect = (breakItem) => {
    setSelectedBreak(breakItem);
    setCurrentStep(BREAK_STEPS.WAIVER_CONFIRMATION);
  };

  const handleStartBreakSubmit = async () => {
    try {
      setIsStartingMealBreak(true);
      await onBreakAction(BREAK_ACTIONS.START_MEAL);
      handleClose();
    } catch (error) {
      console.error('Error starting break:', error);
      showErrorToast('Failed to start break');
      setIsStartingMealBreak(false);
      // Don't close the modal on error so user can retry
    }
  };

  const handleBackToOptions = () => {
    setCurrentStep(BREAK_STEPS.OPTIONS);
    setSelectedBreak(null);
    setReason('');
  };

  const handleBackToSelection = () => {
    setCurrentStep(BREAK_STEPS.WAIVER_BREAK_SELECTION);
    setSelectedBreak(null);
    setReason('');
  };

  const handleClose = () => {
    setCurrentStep(BREAK_STEPS.OPTIONS);
    setSelectedBreak(null);
    setReason('');
    setIsStartingMealBreak(false);
    onClose();
  };

  const handleBreakActionClick = async (action) => {
    // Prevent multiple rapid clicks by checking loading states
    if (isStartingMealBreak || isLoading) {
      return;
    }

    if (action === BREAK_ACTIONS.WAIVE) {
      setCurrentStep(BREAK_STEPS.WAIVER_BREAK_SELECTION);
    } else if (action === BREAK_ACTIONS.START_MEAL) {
      try {
        setIsStartingMealBreak(true);
        // Call getBreakStartViolations function to see if starting the meal break will trigger any violations
        const response = await getBreakStartViolations();
        if (response.data.violations.length > 0) {
          setCurrentStep(BREAK_STEPS.START_BREAK_CONFIRMATION);
          setIsStartingMealBreak(false);
          return;
        }
        await onBreakAction(action);
        handleClose();
      } catch (error) {
        console.error('Error checking break start violations:', error);
        showErrorToast('Failed to check break start violations');
        setIsStartingMealBreak(false);
      }
    } else {
      await onBreakAction(action);
      handleClose();
    }
  };

  const handleWaiveSubmit = async () => {
    if (reason.trim() && selectedBreak) {
      try {
        await onWaiveSubmit(selectedBreak, reason.trim());
        showSuccessToast('Break waived successfully');
        handleClose();
      } catch (error) {
        const errorMessage = error?.message || 'Failed to waive break';
        showErrorToast(errorMessage);
      }
    }
  };

  return (
    <ModalContainer
      open={open}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          style: {
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <ModalContent>
        {/* Header with navigation */}
        <ModalHeader>
          {/* Back Button - Show on all steps */}
          <BackButtonContainer>
            <StyledIconButton
              onClick={getBackButtonAction(currentStep, onClose, handleBackToOptions, handleBackToSelection)}
              size="small"
              aria-label="go back"
            >
              <ArrowBackIcon />
            </StyledIconButton>
          </BackButtonContainer>

          {/* Close Button - Always visible */}
          <CloseButtonContainer>
            <StyledIconButton onClick={handleClose} size="small" aria-label="close">
              <CloseIcon />
            </StyledIconButton>
          </CloseButtonContainer>
        </ModalHeader>

        {/* Modal content based on current step */}
        {currentStep === BREAK_STEPS.OPTIONS && (
          <OptionsStep
            showRestButton={showRestButton}
            showBreakButton={showBreakButton}
            isLoading={isLoading}
            isStartingMealBreak={isStartingMealBreak}
            handleBreakActionClick={handleBreakActionClick}
          />
        )}

        {currentStep === BREAK_STEPS.WAIVER_BREAK_SELECTION && (
          <WaiverBreakSelectionStep
            breaks={breaks}
            isLoadingBreaks={isLoadingBreaks}
            onBreakSelect={handleWaiverBreakSelect}
          />
        )}

        {currentStep === BREAK_STEPS.WAIVER_CONFIRMATION && (
          <WaiverConfirmationStep
            selectedBreak={selectedBreak}
            reason={reason}
            setReason={setReason}
            isLoading={isLoading}
            onWaiveSubmit={handleWaiveSubmit}
          />
        )}

        {currentStep === BREAK_STEPS.START_BREAK_CONFIRMATION && (
          <StartBreakConfirmationStep
            isLoading={isLoading}
            onStartBreakSubmit={handleStartBreakSubmit}
            onCancel={handleBackToOptions}
          />
        )}
      </ModalContent>
    </ModalContainer>
  );
};

BreakOptionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shiftId: PropTypes.string.isRequired,
  fetchBreaks: PropTypes.func.isRequired,
  onBreakAction: PropTypes.func.isRequired,
  onWaiveSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  showRestButton: PropTypes.bool,
  showBreakButton: PropTypes.bool,
  currentBreak: PropTypes.object,
};

export default BreakOptionsModal;
